'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { GeistSans } from '@/app/fonts';
const IS_DEV: boolean = process.env.NODE_ENV !== 'production';

/**
 * Global error boundary — catches errors in the root layout itself.
 * Cannot use any layout components (layout is what crashed) and MUST keep
 * the import surface minimal: anything imported here is part of the
 * crash-recovery code path. Inline styles, no Tailwind utility classes
 * that depend on globals.css loading correctly.
 *
 * Dev-mode addition: renders the structured payload of the thrown value
 * inline (via dynamic import of `serializeForTelemetry`) so a structured
 * error (e.g. the SDK's `PersistenceShapeViolationError` carrying
 * `category` / `path` / `kind`) is visible without opening DevTools.
 * Next.js' default overlay collapses such errors to "[object Object]".
 */
export default function GlobalError(t0) {
  const $ = _c(14);
  const {
    error,
    reset
  } = t0;
  let t1;
  let t2;
  if ($[0] !== error) {
    t1 = () => {
      reportGlobalErrorToTelemetry(error);
    };
    t2 = [error];
    $[0] = error;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100vh",
      padding: "2rem",
      textAlign: "center"
    };
    t4 = <h1 style={{
      fontSize: "1.5rem",
      fontWeight: "bold",
      marginBottom: "1rem"
    }}>Something went wrong</h1>;
    t5 = <p style={{
      color: "#666",
      marginBottom: "1.5rem",
      maxWidth: "400px"
    }}>An unexpected error occurred. Our team has been notified.</p>;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  if ($[6] !== error) {
    t6 = IS_DEV && <DevPayload error={error} />;
    $[6] = error;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      padding: "0.5rem 1.5rem",
      borderRadius: "0.375rem",
      border: "1px solid #ccc",
      cursor: "pointer",
      fontSize: "0.875rem"
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== reset) {
    t8 = <button type="button" onClick={reset} style={t7}>Try again</button>;
    $[9] = reset;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t6 || $[12] !== t8) {
    t9 = <html lang="en" className={GeistSans.variable}><body className={GeistSans.className}><div style={t3}>{t4}{t5}{t6}{t8}</div></body></html>;
    $[11] = t6;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  return t9;
}

/**
 * Reports the boundary error via a dynamically-imported telemetry module.
 * Kept at module scope (not an inline `import()` in the effect) so the React
 * Compiler can memoize `GlobalError` — it cannot lower an import expression
 * inside a component/hook body. The dynamic import still keeps telemetry out
 * of the crash-recovery chunk.
 */
function reportGlobalErrorToTelemetry(error: Error & {
  digest?: string;
}): void {
  // Dynamic import to avoid bundling telemetry in the global error chunk.
  // If telemetry itself is broken, the error still renders to the user.
  import('@/lib/telemetry').then(({
    reportError,
    serializeForTelemetry,
    trackAction
  }) => {
    reportError(error, {
      component: 'GlobalError',
      digest: error.digest ?? 'none'
    });
    trackAction('global_error_boundary_triggered', {
      // Splat structured fields so dashboards can filter even on a
      // global-layout crash. `error.message` may already be
      // "[object Object]" by the time we get here — the structured
      // serialization recovers the original.
      ...serializeForTelemetry(error),
      message: error.message.slice(0, 200)
    });
  }).catch(() => {
    // SILENT-CATCH: telemetry import failed — the error UI still renders.
  });
}

/**
 * Dev-only structured payload renderer. Inline styles only — the root
 * stylesheet may not have loaded if global-error fired during the root
 * layout's render. Field extraction is synchronous and dependency-free
 * (re-implements the bit of `serializeForTelemetry` we need) so this
 * chunk never has to wait on `lib/telemetry`.
 */
function DevPayload(t0) {
  const $ = _c(10);
  const {
    error
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== error) {
    const fields = collectOwnProps(error);
    t3 = "dev-error-payload";
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        marginBottom: "1.5rem",
        maxWidth: "40rem",
        overflow: "auto",
        borderRadius: "0.375rem",
        border: "1px solid #ddd",
        background: "#fafafa",
        padding: "0.75rem",
        textAlign: "left",
        fontSize: "0.75rem",
        color: "#444",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace"
      };
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    t1 = error.digest ?? "no-digest";
    t2 = JSON.stringify(fields, null, 2);
    $[0] = error;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
  }
  const t5 = `GlobalError • ${t1}\n\n${t2}`;
  let t6;
  if ($[6] !== t3 || $[7] !== t4 || $[8] !== t5) {
    t6 = <pre data-testid={t3} style={t4}>{t5}</pre>;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}

/** Tiny local fork of telemetry's `serializeForTelemetry` so this file has
 *  zero static imports from `lib/telemetry` (which may itself be broken
 *  when GlobalError fires). Captures the same `error_*` columns. */
function collectOwnProps(error: Error & {
  digest?: string;
}): Record<string, string> {
  const out: Record<string, string> = {
    error_class: error.name || 'Error',
    error_message: error.message || '(empty)'
  };
  if (error.stack) out.error_stack = error.stack.slice(0, 2000);
  // ALLOW-UNKNOWN: `.cause` is part of the open Error chain shape.
  const cause: unknown = (error as {
    cause?: unknown;
  }).cause;
  if (cause !== undefined) out.error_cause = safeStringify(cause).slice(0, 2000);
  for (const key of Object.getOwnPropertyNames(error)) {
    if (key === 'message' || key === 'name' || key === 'stack' || key === 'cause') continue;
    // ALLOW-UNKNOWN: enumerating arbitrary own properties of a thrown Error — open shape.
    const v: unknown = (error as unknown as Record<string, unknown>)[key];
    if (v === undefined) continue;
    out[`error_${key}`] = typeof v === 'string' ? v : safeStringify(v);
  }
  return out;
}

// ALLOW-UNKNOWN: serializer accepts arbitrary thrown values at the error boundary.
function safeStringify(value: unknown): string {
  const seen = new WeakSet<object>();
  try {
    return JSON.stringify(value, (_k, v) => {
      if (typeof v === 'bigint') return v.toString();
      if (typeof v === 'object' && v !== null) {
        if (seen.has(v)) return '[Circular]';
        seen.add(v);
      }
      return v;
    }) ?? '';
  } catch {
    // SILENT-CATCH: JSON.stringify failure (BigInt outside seen replacer, circular ref, etc.) — fall back to a stable sentinel string for diagnostics.
    return '[Unserializable]';
  }
}